// src/App.js
import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { requestOtp, verifyOtp, fetchMe, fetchParks, fetchAnnouncements } from "./Api";

import Rankings from "./pages/Rankings";
import CarChangePage from "./pages/CarChangePage";
import ContactPage from "./pages/ContactPage";
import ReferralPage from "./pages/ReferralPage";
import FaqPage from "./pages/FaqPage";
import WithdrawalPage from "./pages/WithdrawalPage";

import AdminPanel from "./admin/AdminPanel";

const PUBLIC_BASE = process.env.PUBLIC_URL || "";
/** Logo/favicon degisince bunu artir — tarayici cache kirilsin */
const BRAND_ASSETS_VERSION = "20260915a";
const SITAXI_LOGO_SRC = `${PUBLIC_BASE}/logo_sitaxi.png?v=${encodeURIComponent(BRAND_ASSETS_VERSION)}`;

function parkAssetUrl(parkKey, kind) {
  const key = String(parkKey || "").trim();
  if (!key) return "";
  return `${PUBLIC_BASE}/brands/${key}_${kind}.png?v=${encodeURIComponent(BRAND_ASSETS_VERSION)}`;
}

function parkInitials(label) {
  const parts = String(label || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  const s = String(label || "S").trim();
  return (s.slice(0, 2) || "S").toUpperCase();
}

function applyBrandMeta(label, parkKey) {
  const name = String(label || "").trim() || "sitaxi sürücü";
  document.title = name;

  const fav = parkAssetUrl(parkKey, "favicon");
  const href = fav
    ? fav
    : (() => {
        const initials = parkInitials(name).slice(0, 1);
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="7" fill="#0f1626"/><text x="16" y="22.5" text-anchor="middle" font-family="Arial Black, Arial, sans-serif" font-size="18" font-weight="900" fill="#f6d66b">${initials}</text></svg>`;
        return `data:image/svg+xml,${encodeURIComponent(svg)}`;
      })();

  // Tarayici ilk / sabit favicon linklerini tercih edebildigi icin hepsini kaldir.
  document
    .querySelectorAll("link[rel*='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']")
    .forEach((el) => el.parentNode && el.parentNode.removeChild(el));

  const icon = document.createElement("link");
  icon.rel = "icon";
  icon.type = fav ? "image/png" : "image/svg+xml";
  icon.href = href;
  icon.setAttribute("data-dynamic-brand", "1");
  document.head.appendChild(icon);

  const shortcut = document.createElement("link");
  shortcut.rel = "shortcut icon";
  shortcut.type = icon.type;
  shortcut.href = href;
  shortcut.setAttribute("data-dynamic-brand", "1");
  document.head.appendChild(shortcut);

  if (fav) {
    const apple = document.createElement("link");
    apple.rel = "apple-touch-icon";
    apple.href = href;
    apple.setAttribute("data-dynamic-brand", "1");
    document.head.appendChild(apple);
  }
}

function normalizeBrandLabel(label) {
  return String(label || "").replace(/\bIstanbul\b/g, "İstanbul");
}

function splitBrandLabel(label) {
  const normalized = normalizeBrandLabel(label);
  const parts = normalized
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return { primary: parts[0], secondary: parts.slice(1).join(" ") };
  }
  if (parts.length === 1) {
    return { primary: parts[0], secondary: "" };
  }
  return { primary: "", secondary: "" };
}

function SitaxiLogo({ className = "", alt = "SiTaxi Pro" }) {
  return h("img", { src: SITAXI_LOGO_SRC, alt, className: `sitaxi-logo ${className}`.trim() });
}

function BrandTitleText({ label, className, size = "default" }) {
  const { primary, secondary } = splitBrandLabel(label);
  if (!primary) return null;
  const sizeClass = size === "sm" ? " login-sitaxipro-title--sm" : "";
  return h(
    "div",
    { className: `login-sitaxipro-title${sizeClass}${className ? ` ${className}` : ""}` },
    h("span", { className: "login-sitaxipro-sitaxi" }, primary),
    secondary ? h("span", { className: "login-sitaxipro-pro" }, ` ${secondary}`) : null
  );
}

function h(tag, props, ...children) {
  return React.createElement(tag, props, ...children);
}

function safeJsonParse(s) {
  try {
    return JSON.parse(s || "{}");
  } catch {
    return {};
  }
}

/* =========================
   Helpers
========================= */

function asStr(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function formatMoney2(value) {
  if (value === null || value === undefined || value === "") return "0.00";
  const s = asStr(value).replace(",", ".").trim();

  const n = Number(s);
  if (Number.isFinite(n)) return n.toFixed(2);

  const m = s.match(/-?\d+(?:\.\d+)?/);
  if (m) {
    const n2 = Number(m[0]);
    if (Number.isFinite(n2)) return n2.toFixed(2);
  }
  return s;
}

/** Türkçe para gösterimi: "12.34 ₺" */
function formatMoneyTL(value) {
  return `${formatMoney2(value)} ₺`;
}

function getDeep(obj, path, fallback = "") {
  try {
    const parts = path.split(".");
    let cur = obj;
    for (const p of parts) {
      if (!cur || typeof cur !== "object") return fallback;
      cur = cur[p];
    }
    return cur === undefined || cur === null ? fallback : cur;
  } catch {
    return fallback;
  }
}

function safeDateLabel(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    // örn: 26.01.2026 23:17
    return d.toLocaleString("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

const PAGE_ALIASES = {
  home: "home",
  anasayfa: "home",
  hakkimizda: "home",
  rankings: "rankings",
  siralama: "rankings",
  referral: "referral",
  arkadasinigetir: "referral",
  withdraw: "withdraw",
  paracek: "withdraw",
  car: "car",
  aracdegistir: "car",
  profilayarlari: "car",
  settings: "car",
  profile: "car",
  profil: "car",
  announcements: "announcements",
  duyurular: "announcements",
  faq: "faq",
  sikcasorulansorular: "faq",
  contact: "contact",
  iletisim: "contact",
  yoneticiyleiletisim: "contact",
};

function normalizePage(rawPage) {
  const key = String(rawPage || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9ığüşöçİĞÜŞÖÇ]/g, "");
  return PAGE_ALIASES[key] || "home";
}

function pageFromHash() {
  const raw = (window.location.hash || "").replace(/^#\/?/, "");
  return normalizePage(raw);
}

function setHashFromPage(page) {
  const normalized = normalizePage(page);
  const target = `#/${normalized}`;
  if (window.location.hash !== target) {
    window.location.hash = target;
  }
}

function InfoRow({ label, value }) {
  const v = value === undefined || value === null || value === "" ? "-" : value;
  return h(
    "div",
    { className: "info-row" },
    h("div", { className: "info-label" }, label),
    h("div", { className: "info-value" }, v)
  );
}

/* =========================
   Login (City + Phone + OTP)
========================= */

const OTP_DIGIT_COUNT = 6;
const OTP_RESEND_SECONDS = 60;

function OtpDigitInputs({ value, onChange, disabled }) {
  const refs = React.useRef([]);
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, OTP_DIGIT_COUNT)
    .padEnd(OTP_DIGIT_COUNT, " ")
    .split("")
    .map((c) => (c === " " ? "" : c));

  function setAt(index, ch) {
    const next = digits.map((d, i) => (i === index ? ch : d));
    const joined = next.join("").replace(/\s/g, "");
    onChange(joined.slice(0, OTP_DIGIT_COUNT));
  }

  function onDigitChange(index, e) {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setAt(index, "");
      return;
    }
    if (raw.length > 1) {
      // paste / autofill
      const all = (String(value || "").replace(/\D/g, "").slice(0, index) + raw)
        .replace(/\D/g, "")
        .slice(0, OTP_DIGIT_COUNT);
      onChange(all);
      const focusIdx = Math.min(all.length, OTP_DIGIT_COUNT - 1);
      setTimeout(() => refs.current[focusIdx]?.focus(), 0);
      return;
    }
    setAt(index, raw);
    if (index < OTP_DIGIT_COUNT - 1) {
      setTimeout(() => refs.current[index + 1]?.focus(), 0);
    }
  }

  function onKeyDown(index, e) {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      setAt(index - 1, "");
      refs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      refs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < OTP_DIGIT_COUNT - 1) {
      e.preventDefault();
      refs.current[index + 1]?.focus();
    }
  }

  function onPaste(e) {
    const text = (e.clipboardData || window.clipboardData).getData("text") || "";
    const nums = text.replace(/\D/g, "").slice(0, OTP_DIGIT_COUNT);
    if (!nums) return;
    e.preventDefault();
    onChange(nums);
    const focusIdx = Math.min(nums.length, OTP_DIGIT_COUNT - 1);
    setTimeout(() => refs.current[focusIdx]?.focus(), 0);
  }

  return h(
    "div",
    { className: "otp-digits", onPaste },
    digits.map((d, i) =>
      h("input", {
        key: i,
        ref: (el) => {
          refs.current[i] = el;
        },
        className: "otp-digit",
        inputMode: "numeric",
        autoComplete: i === 0 ? "one-time-code" : "off",
        maxLength: 6,
        value: d,
        disabled,
        "aria-label": `Kod ${i + 1}`,
        onChange: (e) => onDigitChange(i, e),
        onKeyDown: (e) => onKeyDown(i, e),
        onFocus: (e) => e.target.select(),
      })
    )
  );
}

function Login({ onSuccess }) {
  const [parks, setParks] = useState([]);
  const [parkKey, setParkKey] = useState("");
  const [phone, setPhone] = useState("");
  const [step, setStep] = useState("phone"); // phone | otp
  const [otp, setOtp] = useState("");
  const [resendLeft, setResendLeft] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const verifyingRef = React.useRef(false);

  const selectedPark = (parks || []).find((p) => p.key === parkKey) || null;
  const selectedLabel = selectedPark?.label || "";

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchParks();
        const list = data?.parks || [];
        setParks(list);
      } catch (e) {
        setErr(e.message || "Park listesi alınamadı");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedLabel) return;
    localStorage.setItem("park_label", selectedLabel);
    applyBrandMeta(selectedLabel, parkKey);
  }, [selectedLabel, parkKey]);

  useEffect(() => {
    if (resendLeft <= 0) return undefined;
    const t = setInterval(() => {
      setResendLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [resendLeft]);

  function onParkChange(e) {
    const v = e.target.value;
    setParkKey(v);
    setPhone("");
    setErr("");
    if (!v) return;
    localStorage.setItem("park_key", v);
    const lab = (parks || []).find((p) => p.key === v)?.label || v;
    localStorage.setItem("park_label", lab);
    applyBrandMeta(lab, v);
  }

  function finishLogin(data) {
    localStorage.setItem("token", data.token);
    localStorage.setItem("profile", JSON.stringify(data.profile || {}));
    localStorage.setItem("park_key", data.park_key || parkKey);
    const lab =
      (parks || []).find((p) => p.key === (data.park_key || parkKey))?.label ||
      selectedLabel ||
      data.park_key ||
      parkKey;
    localStorage.setItem("park_label", lab);
    applyBrandMeta(lab, data.park_key || parkKey);
    onSuccess();
  }

  async function sendOtp() {
    if (!parkKey) throw new Error("Lütfen şehir seçin.");
    if (!String(phone || "").trim()) throw new Error("Telefon numarası gerekli.");
    const data = await requestOtp(phone, parkKey);
    const cooldown = Number(data?.resend_after) || OTP_RESEND_SECONDS;
    setResendLeft(cooldown);
    setOtp("");
    setStep("otp");
  }

  async function onPhoneSubmit(e) {
    e.preventDefault();
    if (loading) return;
    setErr("");
    setLoading(true);
    try {
      await sendOtp();
    } catch (e2) {
      setErr(e2.message || "SMS gönderilemedi");
    } finally {
      setLoading(false);
    }
  }

  async function onOtpSubmit(e) {
    e.preventDefault();
    if (loading || verifyingRef.current) return;
    setErr("");
    const code = String(otp || "").replace(/\D/g, "");
    if (code.length !== OTP_DIGIT_COUNT) {
      setErr("6 haneli doğrulama kodunu girin.");
      return;
    }
    verifyingRef.current = true;
    setLoading(true);
    try {
      const data = await verifyOtp(phone, code, parkKey);
      finishLogin(data);
    } catch (e2) {
      setErr(e2.message || "Kod doğrulanamadı");
      setLoading(false);
      verifyingRef.current = false;
    }
  }

  async function onResend() {
    if (loading || resendLeft > 0) return;
    setErr("");
    setLoading(true);
    try {
      await sendOtp();
    } catch (e2) {
      setErr(e2.message || "SMS gönderilemedi");
    } finally {
      setLoading(false);
    }
  }

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (step !== "otp" || loading || verifyingRef.current) return;
    const code = String(otp || "").replace(/\D/g, "");
    if (code.length !== OTP_DIGIT_COUNT) return;
    verifyingRef.current = true;
    (async () => {
      setErr("");
      setLoading(true);
      try {
        const data = await verifyOtp(phone, code, parkKey);
        finishLogin(data);
      } catch (e2) {
        setErr(e2.message || "Kod doğrulanamadı");
        setLoading(false);
        verifyingRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, step]);

  const maskedPhone = (() => {
    const d = String(phone || "").replace(/\D/g, "");
    if (d.length < 4) return phone || "";
    return `****${d.slice(-4)}`;
  })();

  return h(
    "div",
    { className: "page login-bg" },
    h(
      "div",
      { className: "card login-card" },
      h(
        "div",
        { className: "login-sitaxipro-title" },
        h(SitaxiLogo, { className: "sitaxi-logo--login" })
      ),
      step === "phone"
        ? h(React.Fragment, null,
            h(
              "form",
              { className: "form login-form", onSubmit: onPhoneSubmit },
              h(
                "div",
                { className: "login-field" },
                h("label", { className: "label", htmlFor: "login-park-select" }, "Şehir / Marka"),
                h(
                  "select",
                  {
                    id: "login-park-select",
                    className: "input",
                    value: parkKey,
                    onChange: onParkChange,
                    disabled: loading,
                  },
                  h("option", { value: "" }, "Şehir / Marka seçin"),
                  (parks || []).map((p) => h("option", { key: p.key, value: p.key }, p.label))
                )
              ),
              h(AnimatePresence, null,
                parkKey
                  ? h(
                      motion.div,
                      {
                        key: "login-brand-block",
                        className: "login-brand-reveal",
                        initial: { opacity: 0, height: 0, y: -8 },
                        animate: { opacity: 1, height: "auto", y: 0 },
                        exit: { opacity: 0, height: 0, y: -8 },
                        transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
                      },
                      h(BrandTitleText, {
                        label: selectedLabel,
                        className: "login-sitaxipro-title--brand",
                      }),
                      h("div", { className: "brand-sub" }, "Sürücü Paneli")
                    )
                  : null
              ),
              h(AnimatePresence, null,
                parkKey
                  ? h(
                      motion.div,
                      {
                        key: "login-phone-block",
                        className: "login-phone-reveal",
                        initial: { opacity: 0, height: 0, y: -10 },
                        animate: { opacity: 1, height: "auto", y: 0 },
                        exit: { opacity: 0, height: 0, y: -10 },
                        transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1], delay: 0.05 },
                      },
                      h(
                        "div",
                        { className: "login-field" },
                        h("label", { className: "label", htmlFor: "login-phone-input" }, "Telefon"),
                        h("input", {
                          id: "login-phone-input",
                          className: "input",
                          value: phone,
                          onChange: (e) => setPhone(e.target.value),
                          placeholder: "+9053xxxxxxxxx",
                          autoComplete: "tel",
                          disabled: loading,
                          autoFocus: true,
                        })
                      ),
                      err ? h("div", { className: "error" }, err) : null,
                      h(
                        "button",
                        { className: "btn", disabled: loading, type: "submit" },
                        loading ? "SMS gönderiliyor..." : "Giriş Yap"
                      )
                    )
                  : null
              )
            )
          )
        : h(React.Fragment, null,
            parkKey
              ? h(
                  "div",
                  { className: "login-brand-reveal login-brand-reveal--static" },
                  h(BrandTitleText, {
                    label: selectedLabel,
                    className: "login-sitaxipro-title--brand",
                  }),
                  h("div", { className: "brand-sub" }, "Sürücü Paneli")
                )
              : null,
            h("h1", { className: "title" }, "SMS Doğrulama"),
            h(
              "p",
              { className: "muted" },
              `Doğrulama kodu ${maskedPhone} numarasına gönderildi.`
            ),
            h(
              "form",
              { className: "form", onSubmit: onOtpSubmit },
              h("label", { className: "label" }, "Doğrulama kodu"),
              h(OtpDigitInputs, {
                value: otp,
                onChange: setOtp,
                disabled: loading,
              }),
              err ? h("div", { className: "error" }, err) : null,
              h(
                "button",
                { className: "btn", disabled: loading || String(otp).replace(/\D/g, "").length !== OTP_DIGIT_COUNT, type: "submit" },
                loading ? "Doğrulanıyor..." : "Doğrula"
              ),
              h(
                "div",
                { className: "otp-actions" },
                h(
                  "button",
                  {
                    type: "button",
                    className: "btn-link",
                    disabled: loading || resendLeft > 0,
                    onClick: onResend,
                  },
                  resendLeft > 0 ? `Kodu tekrar gönder (${resendLeft}s)` : "Kodu tekrar gönder"
                ),
                h(
                  "button",
                  {
                    type: "button",
                    className: "btn-link",
                    disabled: loading,
                    onClick: () => {
                      setStep("phone");
                      setOtp("");
                      setErr("");
                    },
                  },
                  "Telefonu değiştir"
                )
              )
            )
          ),
      h("div", { className: "footer-note" }, "© 2026 SİTAXİ")
    )
  );
}

/* =========================
   Pages
========================= */

function HomeContent({ profile, onNav }) {
  const first = asStr(profile.first_name || "").trim();
  const last = asStr(profile.last_name || "").trim();
  const fullName = (first + " " + last).trim() || "-";

  const phone = getDeep(profile, "phone", "") || getDeep(profile, "person.contact_info.phone", "");

  const workStatus = getDeep(profile, "profile.work_status", "") || getDeep(profile, "work_status", "");
  const driverProfileId = getDeep(profile, "driver_profile_id", "");
  /** Yandex GET v2/parks/contractors/driver-profile → person.driver_license.number */
  const driverLicenseNumber =
    getDeep(profile, "driver_license_number", "") ||
    getDeep(profile, "driver_license.number", "") ||
    "";
  const parkCity =
    getDeep(profile, "park_city", "") ||
    getDeep(profile, "park.city", "") ||
    "";

  const balanceTotal = profile.balance ?? "0.00";
  const balanceYandexReview =
    getDeep(profile, "balance_yandex_review", "") ||
    getDeep(profile, "yandex_pending_balance", "") ||
    "0.00";
  const balanceWithdrawable =
    getDeep(profile, "withdrawable_balance", "") ||
    getDeep(profile, "balance_withdrawable", "") ||
    balanceTotal;

  return h(
    React.Fragment,
    null,

    h(
      "div",
      { className: "topbar" },
      h("div", { className: "hello" }, "Merhaba, ", h("span", { className: "name" }, fullName))
    ),

    h(
      "div",
      { className: "cards cards-balance" },
      h(
        "div",
        { className: "mini-card" },
        h("div", { className: "mini-value" }, formatMoneyTL(balanceTotal)),
        h("div", { className: "mini-label" }, "Toplam Bakiye")
      ),
      h(
        "div",
        { className: "mini-card yellow" },
        h("div", { className: "mini-value" }, formatMoneyTL(balanceYandexReview)),
        h("div", { className: "mini-label" }, "Yandex İncelemesinde Olan Bakiye")
      ),
      h(
        "div",
        { className: "mini-card purple" },
        h("div", { className: "mini-value" }, formatMoneyTL(balanceWithdrawable)),
        h("div", { className: "mini-label" }, "Çekilebilir Bakiye")
      )
    ),

    h(
      "div",
      { className: "home-contract-row" },
      h(
        "button",
        { type: "button", className: "home-support-btn", onClick: () => onNav("contact") },
        "Destek ile iletişime geçin"
      )
    ),

    h(
      "div",
      { className: "card info-card home-profile-card" },
      h(
        "div",
        { className: "card-title", style: { fontWeight: 900, fontSize: 18, marginBottom: 12 } },
        "Bilgilerim"
      ),
      h(
        "div",
        { className: "info-grid-single" },
        h(InfoRow, { label: "Telefon", value: phone || "-" }),
        h(InfoRow, {
          label: "Ehliyeti numarası",
          value: driverLicenseNumber || driverProfileId || "-",
        }),
        h(InfoRow, { label: "Durum", value: workStatus || "-" }),
        h(InfoRow, { label: "Şehir", value: parkCity || "-" })
      )
    )
  );
}

function AnnouncementsPage() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setErr("");
        setLoading(true);

        const token = localStorage.getItem("token") || "";
        if (!token) {
          setItems([]);
          setErr("Giriş yapmanız gerekiyor.");
          return;
        }

        const data = await fetchAnnouncements(); // auth:true
        setItems(data?.items || []);
      } catch (e) {
        setErr(e?.message || "Duyurular alınamadı");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return h(
    React.Fragment,
    null,
    h(
      "div",
      { className: "topbar" },
      h("div", { className: "hello" }, h("span", { className: "name" }, "Duyurular"))
    ),

    h(
      "div",
      { className: "card info-card", style: { marginTop: 18 } },
      loading ? h("div", { className: "muted" }, "Yükleniyor...") : null,
      err ? h("div", { className: "muted", style: { marginTop: 6 } }, err) : null,

      !loading && !err && (!items || items.length === 0)
        ? h("div", { className: "muted" }, "Henüz duyuru yok.")
        : null,

      (items || []).map((x, idx) =>
        h(
          "div",
          {
            key: x.id || idx,
            style: {
              borderTop: idx === 0 ? "0" : "1px solid #eee",
              padding: "16px 0",
            },
          },
          h(
            "div",
            { style: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 } },
            h(
              "div",
              { style: { fontWeight: 900, fontSize: 16, lineHeight: 1.2 } },
              x.title || "Duyuru"
            ),
            h("div", { className: "muted", style: { whiteSpace: "nowrap" } }, safeDateLabel(x.created_at))
          ),
          h("div", { style: { height: 8 } }),
          h("div", { style: { whiteSpace: "pre-wrap", lineHeight: 1.5 } }, x.body || "")
        )
      )
    )
  );
}

/* =========================
   Layout
========================= */

function Home({ profile, page, onNav, onLogout }) {
  const parkKey = localStorage.getItem("park_key") || "";
  const parkLabel = localStorage.getItem("park_label") || parkKey || "sitaxi sürücü";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    applyBrandMeta(parkLabel, parkKey);
  }, [parkLabel, parkKey]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  const mobileItems = [
    { key: "home", label: "Ana Sayfa" },
    { key: "withdraw", label: "Para Çek" },
    { key: "referral", label: "Arkadaşını Getir" },
    { key: "car", label: "Profil Ayarları" },
    { key: "announcements", label: "Duyurular" },
    { key: "faq", label: "Sıkça Sorulanlar" },
    { key: "contact", label: "Yöneticiyle İletişim" },
  ];

  const pageTitle =
    mobileItems.find((it) => it.key === page)?.label ||
    (page === "rankings" ? "Sıralama" : "Ana Sayfa");

  function goMobile(key) {
    setMobileMenuOpen(false);
    onNav(key);
  }

  return h(
    "div",
    { className: "home-wrap driver-shell" },
    h(
      "aside",
      { className: "sidebar" },
      h(SitaxiLogo, { className: "sitaxi-logo--sidebar" }),
      parkLabel
        ? h("div", { className: "sidebar-park-label" }, parkLabel)
        : null,
      h(
        "nav",
        { className: "menu" },
        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (page === "home" ? " active" : ""),
            onClick: () => onNav("home"),
          },
          "Ana sayfa"
        ),
        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (page === "withdraw" ? " active" : ""),
            onClick: () => onNav("withdraw"),
          },
          "Para Cek"
        ),
        // Sıralama tablosu şimdilik menüden gizli
        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (page === "referral" ? " active" : ""),
            onClick: () => onNav("referral"),
          },
          "Arkadaşını Getir",
          h("span", { className: "menu-badge" }, "YENİ")
        ),
        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (page === "car" ? " active" : ""),
            onClick: () => onNav("car"),
          },
          "Profil Ayarları"
        ),

        // ✅ Profil Ayarları'ndan sonra: Duyurular
        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (page === "announcements" ? " active" : ""),
            onClick: () => onNav("announcements"),
          },
          "Duyurular"
        ),

        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (page === "faq" ? " active" : ""),
            onClick: () => onNav("faq"),
          },
          "Sıkça sorulan sorular"
        ),
        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (page === "contact" ? " active" : ""),
            onClick: () => onNav("contact"),
          },
          "Yöneticiyle iletişim"
        )
      ),
      h("button", { className: "btn-outline", onClick: onLogout }, "Çıkış")
    ),

    h(
      "main",
      { className: "content" },
      h(
        "div",
        { className: "mobile-topbar" },
        h(
          "button",
          {
            type: "button",
            className: "mobile-burger",
            "aria-label": "Menüyü aç",
            "aria-expanded": mobileMenuOpen ? "true" : "false",
            onClick: () => setMobileMenuOpen(true),
          },
          h("span", { className: "mobile-burger-line" }),
          h("span", { className: "mobile-burger-line" }),
          h("span", { className: "mobile-burger-line" })
        ),
        h("div", { className: "mobile-topbar-title" }, pageTitle),
        h("div", { className: "mobile-topbar-spacer" })
      ),

      h(AnimatePresence, null,
        mobileMenuOpen
          ? h(
              "div",
              { key: "mobile-drawer", className: "mobile-drawer-root" },
              h(motion.button, {
                type: "button",
                className: "mobile-drawer-backdrop",
                "aria-label": "Menüyü kapat",
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                exit: { opacity: 0 },
                transition: { duration: 0.2 },
                onClick: () => setMobileMenuOpen(false),
              }),
              h(
                motion.aside,
                {
                  className: "mobile-drawer",
                  initial: { x: "-100%" },
                  animate: { x: 0 },
                  exit: { x: "-100%" },
                  transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                },
                h(
                  "div",
                  { className: "mobile-drawer-head" },
                  h(SitaxiLogo, { className: "sitaxi-logo--sidebar" }),
                  parkLabel
                    ? h("div", { className: "sidebar-park-label" }, parkLabel)
                    : null,
                  h(
                    "button",
                    {
                      type: "button",
                      className: "mobile-drawer-close",
                      "aria-label": "Kapat",
                      onClick: () => setMobileMenuOpen(false),
                    },
                    "×"
                  )
                ),
                h("div", { className: "mobile-drawer-sub" }, "Sürücü Paneli"),
                h(
                  "nav",
                  { className: "mobile-drawer-menu" },
                  mobileItems.map((it) =>
                    h(
                      "button",
                      {
                        key: it.key,
                        type: "button",
                        className:
                          "mobile-drawer-item" + (page === it.key ? " active" : ""),
                        onClick: () => goMobile(it.key),
                      },
                      it.label
                    )
                  )
                ),
                h(
                  "button",
                  {
                    type: "button",
                    className: "mobile-drawer-logout",
                    onClick: () => {
                      setMobileMenuOpen(false);
                      onLogout();
                    },
                  },
                  "Çıkış"
                )
              )
            )
          : null
      ),

      page === "home"
        ? h(HomeContent, { profile, onNav })
        : page === "rankings"
        ? h(Rankings, null)
        : page === "referral"
        ? h(ReferralPage, null)
        : page === "withdraw"
        ? h(WithdrawalPage, null)
        : page === "car"
        ? h(CarChangePage, null)
        : page === "announcements"
        ? h(AnnouncementsPage, null)
        : page === "faq"
        ? h(FaqPage, null)
        : h(ContactPage, null)
    )
  );
}

/* =========================
   Admin route wrapper
========================= */

function AdminRoute() {
  return h(AdminPanel, null);
}

/* =========================
   App
========================= */

export default function App() {
  // Domain root: /admin (eski path /sitaxi_driver_panel/admin da desteklenir)
  const pathNorm = (window.location.pathname || "/").replace(/\/+$/, "") || "/";
  const isAdmin = pathNorm === "/admin" || pathNorm.endsWith("/admin");

  // ✅ Hooks her zaman aynı sırada çalışır (admin dahil)
  const [route, setRoute] = useState("loading"); // loading | login | home
  const [page, setPage] = useState(() => pageFromHash()); // home | rankings | referral | bonuses | car | announcements | faq | contact

  const [profile, setProfile] = useState(() => safeJsonParse(localStorage.getItem("profile")));

  useEffect(() => {
    // ✅ admin sayfasında driver auth kontrolü yapma
    if (isAdmin) return;

    const token = localStorage.getItem("token");
    if (!token) {
      setRoute("login");
      return;
    }

    (async () => {
      try {
        const data = await fetchMe(token);
        localStorage.setItem("profile", JSON.stringify(data.profile || {}));
        setProfile(data.profile || {});
        setRoute("home");
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("profile");
        setRoute("login");
      }
    })();
  }, [isAdmin]);

  // Anasayfa bakiyesi: sayfa açıkken / odaklanınca / home'a gelince canlı yenile
  useEffect(() => {
    if (isAdmin || route !== "home") return undefined;

    let cancelled = false;

    async function refreshProfile({ logoutOnAuthFail = false } = {}) {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const data = await fetchMe(token);
        if (cancelled) return;
        localStorage.setItem("profile", JSON.stringify(data.profile || {}));
        setProfile(data.profile || {});
      } catch (err) {
        if (cancelled) return;
        if (logoutOnAuthFail && err && (err.status === 401 || err.status === 403)) {
          localStorage.removeItem("token");
          localStorage.removeItem("profile");
          setProfile({});
          setRoute("login");
        }
      }
    }

    // Ana sayfaya her girişte hemen tazele
    if (page === "home") {
      refreshProfile();
    }

    const POLL_MS = 20000;
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      // bakiye kartları home'da; diğer sayfalarda da güncel kalsın diye poll et
      refreshProfile();
    }, POLL_MS);

    const onFocus = () => refreshProfile();
    const onVis = () => {
      if (document.visibilityState === "visible") refreshProfile();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isAdmin, route, page]);

  function onSuccess() {
    const token = localStorage.getItem("token");
    if (!token) {
      setRoute("login");
      return;
    }
    // Login response zaten profile yazıyor; kullanıcıyı bekletmeden içeri al.
    const cached = safeJsonParse(localStorage.getItem("profile"));
    setProfile(cached || {});
    setRoute("home");
    const p = pageFromHash();
    setPage(p);
    setHashFromPage(p);

    // Arka planda profile tazele.
    (async () => {
      try {
        const data = await fetchMe(token);
        localStorage.setItem("profile", JSON.stringify(data.profile || {}));
        setProfile(data.profile || {});
      } catch {
        // Background refresh hatasında aktif oturumu düşürme.
      }
    })();
  }

  function onLogout() {
    localStorage.removeItem("token");
    localStorage.removeItem("profile");
    setProfile({});
    setRoute("login");
    setPage("home");
    setHashFromPage("home");
  }

  function onNav(next) {
    const normalized = normalizePage(next);
    setPage(normalized);
    setHashFromPage(normalized);
  }

  useEffect(() => {
    if (isAdmin) return;
    const onHashChange = () => setPage(pageFromHash());
    window.addEventListener("hashchange", onHashChange);
    // ilk render'da hash ile senkron
    const p = pageFromHash();
    setPage(p);
    setHashFromPage(p);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [isAdmin]);

  // ✅ Admin render hooklardan sonra
  if (isAdmin) return h(AdminRoute, null);

  if (route === "loading") {
    return h(
      "div",
      { className: "page login-bg" },
      h("div", { className: "card login-card" }, h("div", null, "Yükleniyor..."))
    );
  }

  if (route === "login") return h(Login, { onSuccess });

  return h(Home, { profile, page, onNav, onLogout });
}
